import React, { type ReactNode, createContext, useContext, useState } from 'react';

// Define types for the chart settings
export interface DateRange {
  start: Date;
  end: Date;
}

export type ComparisonMode = 'previous_period' | 'year_over_year' | 'custom' | 'none';

export type ChartColorScheme =
  | 'default'
  | 'brand'
  | 'categorical'
  | 'sequential'
  | 'diverging'
  | 'monochrome';

export interface AnalyticsContextType {
  // Date range settings
  dateRange: DateRange;
  setDateRange: (range: DateRange) => void;

  // Comparison settings
  comparisonMode: ComparisonMode;
  setComparisonMode: (mode: ComparisonMode) => void;
  comparisonDateRange: DateRange | null;
  setComparisonDateRange: (range: DateRange | null) => void;

  // Visual settings
  colorScheme: ChartColorScheme;
  setColorScheme: (scheme: ChartColorScheme) => void;
  showValues: boolean;
  setShowValues: (show: boolean) => void;

  // Other settings
  refresh: () => void;
  isRefreshing: boolean;
}

// Create the context with default values
const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined);

// Create a provider component
export const AnalyticsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Default date range (last 30 days)
  const today = new Date();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(today.getDate() - 30);

  const [dateRange, setDateRange] = useState<DateRange>({
    start: thirtyDaysAgo,
    end: today,
  });

  const [comparisonMode, setComparisonMode] = useState<ComparisonMode>('previous_period');
  const [comparisonDateRange, setComparisonDateRange] = useState<DateRange | null>(null);
  const [colorScheme, setColorScheme] = useState<ChartColorScheme>('brand');
  const [showValues, setShowValues] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Function to refresh all analytics data
  const refresh = (): void => {
    setIsRefreshing(true);
    // Implement data refresh logic
    setTimeout(() => setIsRefreshing(false), 1000);
  };

  // Context value
  const contextValue: AnalyticsContextType = {
    dateRange,
    setDateRange,
    comparisonMode,
    setComparisonMode,
    comparisonDateRange,
    setComparisonDateRange,
    colorScheme,
    setColorScheme,
    showValues,
    setShowValues,
    refresh,
    isRefreshing,
  };

  return <AnalyticsContext.Provider value={contextValue}>{children}</AnalyticsContext.Provider>;
};

// Custom hook to use the analytics context
export const useAnalytics = (): AnalyticsContextType => {
  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};

export default AnalyticsContext;
